import {
  QUESTION_KINDS,
  QUESTIONNAIRE_LIMITS,
  type QuestionInput,
  TRUE_FALSE_OPTION_TEXTS,
} from './questionnaire';

/** The columns of a questions CSV. Options are `option_1` up to `option_<maxOptions>`. */
export const QUESTION_CSV_COLUMNS = {
  type: 'type',
  question: 'question',
  points: 'points',
  correct: 'correct',
  modelAnswer: 'model_answer',
} as const;

export const QUESTION_CSV_OPTION_PREFIX = 'option_';

/** A weak model loses its place in a long reply, so the prompt asks for blocks of this many rows. */
export const QUESTION_CSV_ROWS_PER_BLOCK = 25;

/** How many option columns the prompt asks for; the importer reads up to the questionnaire limit. */
export const QUESTION_CSV_PROMPT_OPTIONS = 6;

export const QUESTION_CSV_OPTION_COLUMNS = Array.from(
  { length: QUESTIONNAIRE_LIMITS.maxOptions },
  (_, index) => `${QUESTION_CSV_OPTION_PREFIX}${index + 1}`,
);

/** Several correct options in one cell, as in `1;3`. */
export const QUESTION_CSV_CORRECT_SEPARATOR = ';';

/** What the importer also takes between correct options, since models vary it. */
export const QUESTION_CSV_CORRECT_SPLIT = /\s*[;,|/]\s*/;

/** Option letters a model writes in place of numbers: A is option 1. */
export const QUESTION_CSV_OPTION_LETTERS = 'abcdefghijkl';

/** Type names models write instead of ours, after lower-casing and joining words with `_`. */
export const QUESTION_CSV_KIND_ALIASES: Record<string, string> = {
  single: 'single_choice',
  mcq: 'single_choice',
  multiple_choice_single: 'single_choice',
  single_select: 'single_choice',
  multiple: 'multiple_choice',
  multi_choice: 'multiple_choice',
  multiple_select: 'multiple_choice',
  multi_select: 'multiple_choice',
  checkbox: 'multiple_choice',
  truefalse: 'true_false',
  true_or_false: 'true_false',
  tf: 'true_false',
  boolean: 'true_false',
  short: 'short_text',
  short_answer: 'short_text',
  text: 'short_text',
  open: 'short_text',
  open_ended: 'short_text',
};

/** What a model writes for true or false, lower case. */
export const QUESTION_CSV_TRUE_WORDS = ['true', 't', 'yes', 'y'];
export const QUESTION_CSV_FALSE_WORDS = ['false', 'f', 'no', 'n'];

/** Header names models write instead of ours, mapped to ours. */
export const QUESTION_CSV_HEADER_ALIASES: Record<string, string> = {
  question_type: 'type',
  kind: 'type',
  question_text: 'question',
  prompt: 'question',
  marks: 'points',
  answer: 'correct',
  correct_answer: 'correct',
  correct_option: 'correct',
  correct_options: 'correct',
  modelanswer: 'model_answer',
  expected_answer: 'model_answer',
};

export const QUESTION_CSV_DEFAULT_POINTS = 1;

/**
 * The most characters the gateway accepts to check: about a hundred ordinary
 * questions. Characters are not bytes, so this alone does not keep a request
 * under the gateway's JSON body limit; `QUESTION_CSV_MAX_REQUEST_BYTES` does.
 */
export const QUESTION_CSV_MAX_LENGTH = 70_000;

/**
 * Just under the gateway's JSON body limit (the Express default, 100 KB). A
 * non-Latin script is three bytes a character and quotes and line breaks double
 * in JSON, so a text within `QUESTION_CSV_MAX_LENGTH` can still exceed it, and
 * the body parser then refuses it before any readable message can be given.
 */
export const QUESTION_CSV_MAX_REQUEST_BYTES = 100_000;

const optionColumns = Array.from(
  { length: QUESTION_CSV_PROMPT_OPTIONS },
  (_, index) => `${QUESTION_CSV_OPTION_PREFIX}${index + 1}`,
);
const firstOption = optionColumns[0];
const lastOption = optionColumns[optionColumns.length - 1];

// The options come last so a row simply stops after its last one: a model
// asked to pad a row with the right number of empty cells miscounts them.
export const QUESTION_CSV_HEADER = [
  QUESTION_CSV_COLUMNS.type,
  QUESTION_CSV_COLUMNS.question,
  QUESTION_CSV_COLUMNS.points,
  QUESTION_CSV_COLUMNS.correct,
  QUESTION_CSV_COLUMNS.modelAnswer,
  ...optionColumns,
].join(',');

const [trueText, falseText] = TRUE_FALSE_OPTION_TEXTS;
const { singleChoice, multipleChoice, trueFalse, shortText } = QUESTION_KINDS;
const and = QUESTION_CSV_CORRECT_SEPARATOR;

const example = [
  QUESTION_CSV_HEADER,
  `"${singleChoice}","Which colour, of these, is a primary colour?","1","2",,"Green","Red","Purple"`,
  `"${multipleChoice}","Which of these are prime numbers?","2","1${and}3",,"2","4","5","9"`,
  `"${trueFalse}","Water boils at 100 °C at sea level.","1","${trueText}",`,
  `"${shortText}","Explain why backups are tested.","3",,"A backup that has never been restored may not work when it is needed."`,
].join('\n');

/**
 * What an author pastes into an AI assistant with their material. Built from
 * the same constants the server's reader uses, so the two cannot disagree.
 * Written for any assistant, so every rule a model has been seen to break is
 * spelled out.
 */
export const QUESTION_CSV_PROMPT = `You are helping me build a training questionnaire for import into our training app. Read the material at the end and return the questions as CSV in exactly the format below. The importer is strict: a row that breaks a rule is refused.

WHAT TO WRITE
- If the material is already a quiz (questions with options and answers), convert it faithfully: keep every question, in the same order, with the same options in the same order and the same right answer. Do not reword, merge, split or drop questions, and do not change a question's type. Remove numbering and letters such as "1." or "A)" from the text.
- Otherwise, write questions that check a person has understood the material. Each must be answerable from the material alone. Keep wrong options plausible. Mix the types.
- At most ${QUESTIONNAIRE_LIMITS.maxQuestions} questions.

OUTPUT
- Return only CSV inside a code block. No text before or after it. Not a Markdown table.
- More than ${QUESTION_CSV_ROWS_PER_BLOCK} questions: split them into several code blocks of up to ${QUESTION_CSV_ROWS_PER_BLOCK} rows, each starting with the header. Never stop in the middle of a row.
- The first line is this header, exactly, with nothing added, removed or renamed:
${QUESTION_CSV_HEADER}
- Then one line per question.

COLUMNS, IN THIS ORDER
1. ${QUESTION_CSV_COLUMNS.type}: exactly one of ${singleChoice}, ${multipleChoice}, ${trueFalse}, ${shortText}.
   - ${singleChoice}: one right answer among 2 to ${QUESTION_CSV_PROMPT_OPTIONS} options (use this for a normal A/B/C/D question).
   - ${multipleChoice}: one or more right answers among 2 to ${QUESTION_CSV_PROMPT_OPTIONS} options.
   - ${trueFalse}: a statement that is either true or false.
   - ${shortText}: a written answer that a person marks.
2. ${QUESTION_CSV_COLUMNS.question}: the question text, at most ${QUESTIONNAIRE_LIMITS.maxPromptLength} characters.
3. ${QUESTION_CSV_COLUMNS.points}: a whole number from 1 to ${QUESTIONNAIRE_LIMITS.maxPoints}. Use 1 unless the material gives marks.
4. ${QUESTION_CSV_COLUMNS.correct}: which answer is right.
   - ${singleChoice}: the number of the right option: 1 for ${firstOption}, 2 for the next, and so on. A digit, never a letter or the option's text. If the material says "B", write 2.
   - ${multipleChoice}: the numbers of every right option joined by ${and} with no spaces, e.g. 1${and}3. Never join them with a comma.
   - ${trueFalse}: ${trueText} or ${falseText}.
   - ${shortText}: leave empty.
5. ${QUESTION_CSV_COLUMNS.modelAnswer}: for ${shortText} only, the answer a marker compares against. Leave empty for every other type.
6. ${firstOption} to ${lastOption}: the options, for ${singleChoice} and ${multipleChoice} only, each at most ${QUESTIONNAIRE_LIMITS.maxOptionLength} characters. Write them one after another and stop after the last one: do not add empty cells after it. A ${trueFalse} or ${shortText} row has no options, so it ends after ${QUESTION_CSV_COLUMNS.modelAnswer}.

CSV RULES
- Separate cells with commas.
- Put double quotes around every cell that is not empty, including type, points and correct, as in the example.
- Use straight double quotes ("), never curly ones (“ ”).
- A double quote inside text is written twice: "Click ""Report Phishing"" in Outlook".
- An empty cell is nothing between two commas. Never write N/A, none or a space.

CHECK BEFORE YOU ANSWER
- Each ${singleChoice} row has exactly one number in ${QUESTION_CSV_COLUMNS.correct}, and that option exists.
- Each ${multipleChoice} row's numbers all point at options that exist.
- No row has more cells than the header has columns, and every non-empty cell is quoted.
- ${trueFalse} and ${shortText} rows have nothing after ${QUESTION_CSV_COLUMNS.modelAnswer}; choice rows leave ${QUESTION_CSV_COLUMNS.modelAnswer} empty.

EXAMPLE
${example}

NUMBER OF QUESTIONS: [e.g. 10, or "all" to convert an existing quiz]

THE MATERIAL:
[paste the policy, notes or quiz text here, or attach the document]
`;

/** One line of what the author pasted, with what the check made of it. */
export interface QuestionsCsvRow {
  /** The data row, counted from 1 after the header; a repeated header is not a row. */
  row: number;
  /** The first words of the question, or of the raw row when there is no question. */
  excerpt: string;
  status: 'ready' | 'problem';
  /** Every reason the row is refused, in plain words; empty when it is ready. */
  problems: string[];
}

export interface ReadQuestionsCsvInput {
  csv: string;
  /** How many questions the draft already holds, so the import stays inside the limit. */
  existingCount: number;
}

export interface QuestionsCsvResult {
  rows: QuestionsCsvRow[];
  /** The rows that are ready, in order; add them only when nothing has a problem. */
  questions: QuestionInput[];
  /** Problems with the file as a whole rather than one row. */
  fileProblems: string[];
  summary: { ready: number; withProblems: number };
  /** Text to paste back into the AI chat that wrote the CSV; empty when nothing is wrong. */
  fixRequest: string;
}

export interface ReadQuestionsCsvPayload {
  id: string;
  number: number;
  input: ReadQuestionsCsvInput;
}
